import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export interface HomeStats {
  /** Unika aktiva elavtal från avtalsflödet. */
  agreementCount: number;
  /** Unika elhandelsbolag med aktiva avtal. */
  companyCount: number;
}

const EMPTY: HomeStats = { agreementCount: 0, companyCount: 0 };

/** Färska i 10 minuter; därefter visas det gamla värdet medan ett nytt hämtas. */
export const FRESH_MS = 10 * 60 * 1000;
/** Längre än så här får sidan inte vänta på siffrorna – då visas texten utan dem. */
export const MAX_WAIT_MS = 1200;

let cache: HomeStats | null = null;
let cacheAt = 0;
let inflight: Promise<HomeStats> | null = null;

function supabaseEnv() {
  const url = process.env["SUPABASE_URL"] ?? process.env["VITE_SUPABASE_URL"];
  const key =
    process.env["SUPABASE_PUBLISHABLE_KEY"] ?? process.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
  return url && key ? { url, key } : null;
}

/** Ett anrop: databasfunktionen home_stats() (migration 0092). */
async function fetchViaRpc(url: string, key: string): Promise<HomeStats | null> {
  const client = createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
  const { data, error } = await client.rpc("home_stats");
  if (error || !data) return null;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { agreement_count?: number | string; company_count?: number | string }
    | undefined;
  if (!row) return null;
  return {
    agreementCount: Number(row.agreement_count) || 0,
    companyCount: Number(row.company_count) || 0,
  };
}

/** Reserv om funktionen saknas: räkna bolagen ur raderna (långsamt, ~10 anrop). */
async function fetchViaRows(url: string, key: string): Promise<HomeStats | null> {
  const client = createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
  // Endast privatavtal – företagsavtal räknas inte med.
  const { count: agreementCount, error: agreementError } = await client
    .from("agreement_feed_items")
    .select("id", { count: "exact", head: true })
    .eq("is_private", true);
  if (agreementError) return null;

  const companies = new Set<string>();
  const pageSize = 1000;
  for (let from = 0; from < 30000; from += pageSize) {
    const { data, error } = await client
      .from("agreement_feed_items")
      .select("company_name")
      .eq("is_private", true)
      .range(from, from + pageSize - 1);
    if (error) break;
    for (const row of data ?? []) {
      const name = row.company_name?.trim().toLowerCase();
      if (name) companies.add(name);
    }
    if (!data || data.length < pageSize) break;
  }
  return { agreementCount: agreementCount ?? 0, companyCount: companies.size };
}

async function refresh(): Promise<HomeStats> {
  const env = supabaseEnv();
  if (!env) return cache ?? EMPTY;
  const stats =
    (await fetchViaRpc(env.url, env.key).catch(() => null)) ??
    (await fetchViaRows(env.url, env.key).catch(() => null));
  if (stats && stats.agreementCount > 0) {
    cache = stats;
    cacheAt = Date.now();
    return stats;
  }
  return cache ?? EMPTY;
}

/** Startar en hämtning, eller återanvänder en som redan pågår. */
function refreshOnce(): Promise<HomeStats> {
  if (!inflight) {
    inflight = refresh().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

export async function fetchHomeStats(): Promise<HomeStats> {
  if (cache) {
    // Gamla siffror duger medan nya hämtas i bakgrunden.
    if (Date.now() - cacheAt >= FRESH_MS) void refreshOnce();
    return cache;
  }
  // Kall instans: vänta en kort stund, annars renderas sidan utan siffror
  // (agreementsPhrase har en formulering för det) och hämtningen fortsätter.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<HomeStats>((resolve) => {
    timer = setTimeout(() => resolve(EMPTY), MAX_WAIT_MS);
  });
  try {
    return await Promise.race([refreshOnce(), timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Endast för tester. */
export function resetHomeStatsCache() {
  cache = null;
  cacheAt = 0;
  inflight = null;
}

export const getHomeStats = createServerFn({ method: "GET" }).handler(async (): Promise<HomeStats> => {
  return fetchHomeStats();
});
